import { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import {
  Activity,
  ArrowRight,
  Clock,
  CreditCard,
  Download,
  RefreshCw,
  TrendingUp,
  UserCheck,
  UserPlus,
  Users,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: 'This Week' },
  { id: 'month', label: 'This Month' },
  { id: '90d', label: '90 Days' },
  { id: 'all_time', label: 'All Time' },
];

const COLORS = ['#D4AF37', '#22c55e', '#3b82f6', '#a855f7', '#f97316', '#ef4444'];

const tooltipStyle = {
  background: '#0B0E14',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 10,
  color: '#fff',
  fontSize: 12,
};

export default function UserAnalyticsPage() {
  const [period, setPeriod] = useState('month');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [rangeActive, setRangeActive] = useState(false);
  const [rangeVersion, setRangeVersion] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, [period, rangeActive, rangeVersion]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = `/api/admin/analytics/user-analytics?period=${period}`;
      if (rangeActive && customRange.start && customRange.end) {
        url = `/api/admin/analytics/user-analytics?start=${encodeURIComponent(customRange.start)}&end=${encodeURIComponent(customRange.end)}`;
      }
      const res = await api.get(url);
      setData(res.data || {});
    } catch (err) {
      console.error(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const k = data?.kpis || {};
  const funnel = data?.funnel || [];
  const series = useMemo(() => (data?.series || []).map((row) => ({
    ts: row.ts,
    Signups: row.signups || 0,
    Active: row.active_users || 0,
    '1st Deposit': row.first_deposit_users || 0,
    '2nd Deposit': row.second_deposit_users || 0,
    '3rd Deposit': row.third_deposit_users || 0,
    Referred: row.referred || 0,
    Organic: row.organic || 0,
  })), [data]);

  const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const fmtPct = (v) => `${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
  const fmtNumber = (v) => Number(v || 0).toLocaleString();

  const exportCSV = () => {
    const rows = [
      ['Metric', 'Value'],
      ['Total Users', k.total_users || 0],
      ['New Signups', k.new_signups || 0],
      ['Active Users', k.active_users || 0],
      ['First Deposit Users', k.first_deposit_users || 0],
      ['Second Deposit Users', k.second_deposit_users || 0],
      ['Third Deposit Users', k.third_deposit_users || 0],
      ['First Deposit %', k.first_deposit_pct || 0],
      ['Second Deposit %', k.second_deposit_pct || 0],
      ['Third Deposit %', k.third_deposit_pct || 0],
      ['Avg Time To First Deposit Hours', k.avg_hours_to_first_deposit || 0],
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `teamlobby-user-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="user-analytics-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="w-6 h-6 text-[#D4AF37]" /> User Analytics
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Deep signup, activation, deposit conversion, referral, retention, and user quality analytics.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCSV} className="px-3 py-2 rounded-lg border border-white/10 text-gray-300 hover:text-white hover:bg-white/5 text-sm flex items-center gap-2">
            <Download className="w-4 h-4" /> Export KPI CSV
          </button>
          <button onClick={fetchData} className="p-2 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:bg-white/5">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#131822] p-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold">Cohort Window</p>
          <p className="text-xs text-gray-500 mt-1">Deposit funnel uses users who signed up in this selected window.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-xl bg-[#0B0E14] border border-white/10 p-1">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                onClick={() => { setPeriod(p.id); setRangeActive(false); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                  !rangeActive && period === p.id ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <input
            type="date"
            value={customRange.start ? customRange.start.slice(0, 10) : ''}
            onChange={(e) => setCustomRange((r) => ({ ...r, start: e.target.value ? `${e.target.value}T00:00:00.000Z` : '' }))}
            className="bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white"
          />
          <input
            type="date"
            value={customRange.end ? customRange.end.slice(0, 10) : ''}
            onChange={(e) => setCustomRange((r) => ({ ...r, end: e.target.value ? `${e.target.value}T23:59:59.999Z` : '' }))}
            className="bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white"
          />
          <button
            onClick={() => {
              if (!customRange.start || !customRange.end) return;
              setRangeActive(true);
              setRangeVersion((v) => v + 1);
            }}
            disabled={!customRange.start || !customRange.end}
            className="px-4 py-1.5 rounded-lg bg-[#D4AF37] text-black text-xs font-semibold disabled:opacity-40"
          >
            Apply
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-5 gap-4">
        <Kpi icon={Users} label="Total Users" value={fmtNumber(k.total_users)} sub={`${fmtNumber(k.new_signups)} new in cohort`} color="#3b82f6" />
        <Kpi icon={Activity} label="Active Users" value={fmtNumber(k.active_users)} sub={`${fmtPct(k.active_rate_pct)} active rate`} color="#22c55e" />
        <Kpi icon={CreditCard} label="1st Deposit" value={fmtNumber(k.first_deposit_users)} sub={`${fmtPct(k.first_deposit_pct)} of signups`} color="#D4AF37" />
        <Kpi icon={CreditCard} label="2nd Deposit" value={fmtNumber(k.second_deposit_users)} sub={`${fmtPct(k.second_after_first_pct)} after first`} color="#a855f7" />
        <Kpi icon={CreditCard} label="3rd Deposit" value={fmtNumber(k.third_deposit_users)} sub={`${fmtPct(k.third_after_second_pct)} after second`} color="#f97316" />
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi icon={UserPlus} label="Referred Signups" value={fmtNumber(k.referred_signups)} sub={`${fmtPct(k.referral_signup_pct)} referral share`} color="#22c55e" />
        <Kpi icon={UserCheck} label="Verified Email" value={fmtNumber(k.email_verified_users)} sub={`${fmtPct((k.email_verified_users || 0) / Math.max(k.total_users || 0, 1) * 100)} of users`} color="#38bdf8" />
        <Kpi icon={UserCheck} label="Verified Phone" value={fmtNumber(k.phone_verified_users)} sub={`${fmtPct((k.phone_verified_users || 0) / Math.max(k.total_users || 0, 1) * 100)} of users`} color="#10b981" />
        <Kpi icon={Clock} label="Avg Time To 1st Deposit" value={`${Number(k.avg_hours_to_first_deposit || 0).toLocaleString()}h`} sub="signup to first approved deposit" color="#f59e0b" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 rounded-2xl border border-white/10 bg-[#131822] p-5">
          <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#D4AF37]" /> Signup And Deposit Cohort Trend
          </h3>
          {series.length ? (
            <ResponsiveContainer width="100%" height={310}>
              <AreaChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                <XAxis dataKey="ts" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="Signups" stroke="#3b82f6" fill="#3b82f633" strokeWidth={2} />
                <Area type="monotone" dataKey="1st Deposit" stroke="#D4AF37" fill="#D4AF3733" strokeWidth={2} />
                <Area type="monotone" dataKey="2nd Deposit" stroke="#a855f7" fill="#a855f733" strokeWidth={2} />
                <Area type="monotone" dataKey="3rd Deposit" stroke="#f97316" fill="#f9731633" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyBox text="No user cohort data in this range" />}
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#131822] p-5">
          <h3 className="text-sm font-semibold text-gray-300 mb-4">Deposit Funnel</h3>
          <div className="space-y-3">
            {funnel.map((step, index) => (
              <div key={step.step} className="rounded-xl bg-[#0B0E14] border border-white/5 p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: `${COLORS[index % COLORS.length]}22`, color: COLORS[index % COLORS.length] }}>{index + 1}</span>
                    <span className="text-sm text-white font-medium">{step.step}</span>
                  </div>
                  <span className="text-sm font-bold text-[#D4AF37]">{fmtNumber(step.users)}</span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.min(step.pct_of_signups || 0, 100)}%`, background: COLORS[index % COLORS.length] }} />
                </div>
                <div className="mt-2 flex items-center justify-between text-[10px] text-gray-500">
                  <span>{fmtPct(step.pct_of_signups)} of signups</span>
                  <span>{index ? `${fmtPct(step.step_conversion_pct)} step conversion` : 'cohort base'}</span>
                </div>
                {step.amount > 0 && <div className="text-[10px] text-gray-500 mt-1">Deposit amount: {fmtMoney(step.amount)}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <SegmentCard title="Acquisition Mix" data={data?.segments?.acquisition || []} />
        <SegmentCard title="Deposit Depth" data={data?.segments?.deposit_status || []} />
        <SegmentCard title="VIP Tier Mix" data={data?.segments?.tiers || []} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <TableCard title="Top Depositors In Cohort" rows={data?.top_depositors || []} type="top" fmtMoney={fmtMoney} />
        <TableCard title="Newest Signups" rows={data?.newest_signups || []} type="newest" fmtMoney={fmtMoney} />
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#131822] p-4">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${color}18` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] text-gray-500 truncate">{label}</p>
          <p className="text-xl font-bold text-white">{value}</p>
          {sub && <p className="text-[10px] text-gray-500 truncate">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

function SegmentCard({ title, data }) {
  const total = data.reduce((sum, row) => sum + Number(row.value || 0), 0);
  return (
    <div className="rounded-2xl border border-white/10 bg-[#131822] p-5">
      <h3 className="text-sm font-semibold text-gray-300 mb-4">{title}</h3>
      {data.length ? (
        <>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={72} paddingAngle={3}>
                {data.map((entry, index) => <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-2 mt-2">
            {data.map((row, index) => (
              <div key={row.name} className="flex items-center justify-between text-xs">
                <span className="text-gray-400 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: COLORS[index % COLORS.length] }} />
                  {row.name}
                </span>
                <span className="text-white font-semibold">{Number(row.value || 0).toLocaleString()} <span className="text-gray-500">({total ? Math.round(row.value / total * 100) : 0}%)</span></span>
              </div>
            ))}
          </div>
        </>
      ) : <EmptyBox text="No segment data" />}
    </div>
  );
}

function TableCard({ title, rows, type, fmtMoney }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#131822] p-5">
      <h3 className="text-sm font-semibold text-gray-300 mb-4">{title}</h3>
      {rows.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500 border-b border-white/5">
                <th className="py-2 pr-3">Player</th>
                <th className="py-2 pr-3">Signup</th>
                <th className="py-2 pr-3">Deposits</th>
                <th className="py-2 pr-3">First</th>
                <th className="py-2 pr-3">Total</th>
                <th className="py-2">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.slice(0, 12).map((row) => (
                <tr key={row.user_id} className="text-gray-300">
                  <td className="py-2 pr-3">
                    <div className="font-semibold text-white max-w-[170px] truncate">{row.username}</div>
                    <div className="text-[10px] text-gray-500 max-w-[170px] truncate">{row.email}</div>
                  </td>
                  <td className="py-2 pr-3 text-xs text-gray-400">{row.created_at ? String(row.created_at).slice(0, 10) : '-'}</td>
                  <td className="py-2 pr-3">{row.deposit_count}</td>
                  <td className="py-2 pr-3">{fmtMoney(row.first_deposit_amount)}</td>
                  <td className="py-2 pr-3 text-[#D4AF37] font-semibold">{fmtMoney(row.deposit_total)}</td>
                  <td className="py-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] ${row.is_referred ? 'bg-green-500/10 text-green-300' : 'bg-blue-500/10 text-blue-300'}`}>
                      {row.is_referred ? 'Referred' : 'Organic'} {type === 'newest' && row.active_in_period ? <ArrowRight className="w-3 h-3" /> : null}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <EmptyBox text="No players in this cohort" />}
    </div>
  );
}

function EmptyBox({ text }) {
  return (
    <div className="h-40 rounded-xl border border-dashed border-white/10 flex items-center justify-center text-sm text-gray-500">
      {text}
    </div>
  );
}
